import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";

const emptyForm = {
  id: null,
  mansione: "",
  employeeId: "",
  localeId: "",
  date: "",
  pranzo: false,
  cena: false,
};

const toLocalInputDate = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const toLocalDateTimeString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${y}-${m}-${d}T${hours}:${minutes}:${seconds}`;
};

const buildDateTimeFromFascia = (dateStr, fascia) => {
  if (!dateStr) return null;
  
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  
  if (fascia === "PRANZO") {
    date.setHours(11, 0, 0, 0);
  } else if (fascia === "CENA") {
    date.setHours(18, 0, 0, 0);
  }
  
  return toLocalDateTimeString(date);
};

const buildEndTimeFromFascia = (dateStr, fascia) => {
  if (!dateStr) return null;
  
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  
  if (fascia === "PRANZO") {
    date.setHours(15, 0, 0, 0);
  } else if (fascia === "CENA") {
    date.setHours(23, 30, 0, 0);
  }
  
  return toLocalDateTimeString(date);
};

const toLocalInputDateTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${y}-${m}-${d}T${hours}:${minutes}`;
};

const toIso = (value) => {
  if (!value) {
    return null;
  }

  return toLocalDateTimeString(new Date(value));
};

export default function ShiftModal({ open, onClose, onSave, onDelete, employees = [], locali = [], shift }) {
  const [form, setForm] = useState(emptyForm);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!shift) {
      setForm(emptyForm);
      return;
    }

    const startDate = new Date(shift.start || shift.extendedProps?.originalStart || new Date());
    const endDate = new Date(shift.end || shift.extendedProps?.originalEnd || new Date());
    const startMinutes = startDate.getHours() * 60 + startDate.getMinutes();
    const endMinutes = endDate.getHours() * 60 + endDate.getMinutes();
    
    const isPranzo = startMinutes >= 11 * 60 && startMinutes < 15 * 60;
    const isCena = startMinutes >= 18 * 60 && startMinutes < 23 * 60 + 30;

    setForm({
      id: shift.id || null,
      mansione: shift.mansione || shift.title || shift?.extendedProps?.mansione || "",
      employeeId: String(shift.employeeId || shift?.extendedProps?.employeeId || ""),
      localeId: String(shift.localeId || shift?.extendedProps?.localeId || ""),
      date: shift.date || toLocalInputDate(shift.start || shift.extendedProps?.originalStart),
      pranzo: isPranzo,
      cena: isCena,
    });
  }, [shift]);

  const isValid = useMemo(() => {
    return (
      !!form.employeeId &&
      !!form.localeId &&
      !!form.mansione &&
      !!form.date &&
      (form.pranzo || form.cena)
    );
  }, [form]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = () => {
    if (!isValid) {
      return;
    }

    // Determina la fascia selezionata (priorità a PRANZO se entrambe sono selezionate)
    const selectedFascia = form.pranzo ? "PRANZO" : form.cena ? "CENA" : null;
    
    if (!selectedFascia) {
      return;
    }

    const start = buildDateTimeFromFascia(form.date, selectedFascia);
    const end = buildEndTimeFromFascia(form.date, selectedFascia);

    onSave({
      id: form.id,
      mansione: form.mansione.trim(),
      employeeId: Number(form.employeeId),
      localeId: Number(form.localeId),
      start,
      end,
    });
  };

  const handleDelete = () => {
    if (!form.id) {
      return;
    }

    if (!window.confirm("Sei sicuro di voler eliminare questo turno? Questa azione non è reversibile.")) {
      return;
    }

    setDeleting(true);
    if (onDelete) {
      onDelete(form.id);
    }
    setDeleting(false);
  }; 

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{form.id ? "Modifica turno" : "Nuovo turno"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              select
              label="Dipendente"
              value={form.employeeId}
              onChange={handleChange("employeeId")}
              fullWidth
              required
            >
              {employees.map((employee) => (
                <MenuItem key={employee.id} value={String(employee.id)}>
                  {employee.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Locale"
              value={form.localeId}
              onChange={handleChange("localeId")}
              fullWidth
              required
            >
              {locali.map((locale) => (
                <MenuItem key={locale.id} value={String(locale.id)}>
                  {locale.nome} ({locale.codice || "N/A"})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Mansione" value={form.mansione} onChange={handleChange("mansione")} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Data turno"
              type="date"
              value={form.date}
              onChange={handleChange("date")}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <div style={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px", color: "#333" }}>
              Fascie di disponibilità <span style={{ color: "#e74c3c" }}>*</span>
            </div>
            <div style={{ display: "flex", gap: "20px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.pranzo}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        pranzo: event.target.checked,
                        cena: !event.target.checked ? prev.cena : false,
                      }))
                    }
                  />
                }
                label="PRANZO (11:00 - 15:00)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.cena}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        cena: event.target.checked,
                        pranzo: !event.target.checked ? prev.pranzo : false,
                      }))
                    }
                  />
                }
                label="CENA (18:00 - 23:30)"
              />
            </div>
            {!form.pranzo && !form.cena && (
              <div style={{ color: "#e74c3c", fontSize: "12px", marginTop: "4px" }}>
                Seleziona almeno una fascia
              </div>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {form.id && (
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            Elimina
          </Button>
        )}
        <Button onClick={onClose}>Annulla</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!isValid}>
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
}
